import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from './common/Catalyst/dialog';
import {
  Field,
  FieldGroup,
  Fieldset,
  Label,
} from './common/Catalyst/fieldset';
import { FieldPicker } from './common/Custom/FieldPicker';
import { Input } from './common/Catalyst/input';
import { Button } from './common/Catalyst/button';
import { Text } from './common/Catalyst/text';
import ShowAlert from './ShowAlert';
import LoadingSpinner from './LoadingSpinner';
import { bankService, ledgerService, vendorService } from './clients/ApiClients';
import { CheckReturn } from './clients/rust-api-client';
import { getBankErrorParts } from './utils/bankHelpers';

interface CheckDialogProps {
  open: boolean;
  onClose: () => void;
  hoaId: string;
  /** One past the highest recorded check number; editable. */
  suggestedCheckNumber: string;
  onRecorded: (check: CheckReturn) => void;
}

interface AlertState {
  title: string;
  message: string;
}

interface AcctOption {
  id: string;
  accountNumber: string;
  purpose?: string | null;
}

interface GlOption {
  id: string;
  number: string;
  name: string;
}

interface FundOption {
  id: string;
  code: string;
  name: string;
}

interface VendorOption {
  id: string;
  name: string;
}

const OTHER_PAYEE = '__other__';
/**
 * "Do not post" is a real choice whose state is `''`, but FieldPicker reads
 * `''` as nothing chosen — so it carries a sentinel in the picker only.
 */
const NO_EXPENSE = '__none__';

const purposeLabel = (purpose?: string | null): string =>
  purpose ? purpose.charAt(0).toUpperCase() + purpose.slice(1) : 'Account';

const todayIso = (): string => new Date().toISOString().slice(0, 10);

/**
 * Record an issued check.
 *
 * One action does three things on the server: inserts the register row,
 * posts the journal entry (debit the expense, credit the cash account
 * linked to the bank account) and registers the check with the bank's
 * Positive Pay service. A bank refusal comes back as a `failed` row, not
 * an error — the check is in the books either way.
 */
const CheckDialog: React.FC<CheckDialogProps> = ({
  open,
  onClose,
  hoaId,
  suggestedCheckNumber,
  onRecorded,
}) => {
  const [accounts, setAccounts] = useState<AcctOption[]>([]);
  const [expenseAccounts, setExpenseAccounts] = useState<GlOption[]>([]);
  const [funds, setFunds] = useState<FundOption[]>([]);
  const [vendors, setVendors] = useState<VendorOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [accountId, setAccountId] = useState('');
  const [checkNumber, setCheckNumber] = useState('');
  const [amount, setAmount] = useState('');
  const [issueDate, setIssueDate] = useState(todayIso());
  const [payeeChoice, setPayeeChoice] = useState(OTHER_PAYEE);
  const [payeeName, setPayeeName] = useState('');
  const [memo, setMemo] = useState('');
  const [expenseId, setExpenseId] = useState('');
  const [fundId, setFundId] = useState('');

  const [error, setError] = useState<AlertState | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const resetForm = useCallback(() => {
    setAccountId('');
    setCheckNumber(suggestedCheckNumber);
    setAmount('');
    setIssueDate(todayIso());
    setPayeeChoice(OTHER_PAYEE);
    setPayeeName('');
    setMemo('');
    setExpenseId('');
    setError(null);
  }, [suggestedCheckNumber]);

  // Load the pickers whenever opened. All four are store-only reads, so
  // they work locally where live bank calls do not.
  useEffect(() => {
    if (!open) return;
    resetForm();
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const [acctResp, glResp, fundResp, vendorResp] = await Promise.all([
          bankService.listHoaAccounts({ hoaId }),
          ledgerService.listAccounts({ hoaId }),
          ledgerService.listFunds({ hoaId }),
          vendorService.getVendorsByHoa({ hoaId }),
        ]);
        if (cancelled) return;
        const active = acctResp.data.accounts.filter((a) => a.is_active);
        setAccounts(
          active.map((a) => ({
            id: a.id,
            accountNumber: a.account_number,
            purpose: a.purpose,
          }))
        );
        if (active.length === 1) setAccountId(active[0].id);
        setExpenseAccounts(
          glResp.data.accounts
            .filter((a) => a.account_type === 'expense' && a.is_active)
            .sort((a, b) => a.account_number.localeCompare(b.account_number))
            .map((a) => ({
              id: a.id,
              number: a.account_number,
              name: a.name,
            }))
        );
        const activeFunds = fundResp.data.funds.filter((f) => f.is_active);
        setFunds(
          activeFunds.map((f) => ({ id: f.id, code: f.code, name: f.name }))
        );
        const operating = activeFunds.find((f) => f.code === 'operating');
        setFundId(operating?.id ?? activeFunds[0]?.id ?? '');
        setVendors(
          vendorResp.data.vendors
            .map((v) => ({ id: v.id, name: v.name }))
            .sort((a, b) => a.name.localeCompare(b.name))
        );
      } catch (err) {
        if (cancelled) return;
        const { body } = getBankErrorParts(err);
        setLoadError(body || "Couldn't load this HOA’s accounts.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, hoaId, resetForm]);

  const selectedVendor = useMemo(
    () => vendors.find((v) => v.id === payeeChoice),
    [vendors, payeeChoice]
  );
  const effectivePayee =
    payeeChoice === OTHER_PAYEE ? payeeName.trim() : selectedVendor?.name ?? '';

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleSubmit = async () => {
    setError(null);
    if (!accountId) {
      setError({ title: 'Choose an account', message: 'Which account was the check drawn on?' });
      return;
    }
    if (!/^\d+$/.test(checkNumber.trim())) {
      setError({ title: 'Check number', message: 'Digits only, as printed on the check.' });
      return;
    }
    const amt = Number(amount);
    if (!amount || isNaN(amt) || amt <= 0) {
      setError({ title: 'Enter an amount', message: 'Amount must be greater than zero.' });
      return;
    }
    if (!issueDate) {
      setError({ title: 'Issue date', message: 'When was the check written?' });
      return;
    }
    if (!effectivePayee) {
      setError({ title: 'Payee', message: 'Who is the check to?' });
      return;
    }
    try {
      setSubmitting(true);
      const resp = await bankService.recordHoaCheck({
        hoaId,
        checkCreate: {
          hoa_bank_account_id: accountId,
          check_number: checkNumber.trim(),
          amount: amount.trim(),
          issue_date: issueDate,
          payee_name: effectivePayee,
          memo: memo.trim() || null,
          vendor_id: payeeChoice === OTHER_PAYEE ? null : payeeChoice,
          expense_gl_account_id: expenseId || null,
          fund_id: fundId || null,
        },
      });
      onRecorded(resp.data);
      onClose();
    } catch (err) {
      const { status, body } = getBankErrorParts(err);
      setError({
        title:
          status === 409
            ? 'Already recorded'
            : status === 400
              ? 'Check not accepted'
              : "Couldn't record the check",
        message: body || 'Please try again.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>Record Check</DialogTitle>
      <DialogDescription>
        Posts the check to the books and registers it with the bank so it
        clears without an exception.
      </DialogDescription>
      <DialogBody>
        {error && (
          <div className="mb-4">
            <ShowAlert
              alertType="error"
              alertTitle={error.title}
              alertMessage={error.message}
              isVisible={!!error}
              onClose={() => setError(null)}
              timeout={12000}
              isInline={true}
            />
          </div>
        )}

        {loading ? (
          <LoadingSpinner />
        ) : loadError ? (
          <ShowAlert
            alertType="error"
            alertTitle="Couldn't load"
            alertMessage={loadError}
            isVisible={!!loadError}
            onClose={() => setLoadError(null)}
            timeout={12000}
            isInline={true}
          />
        ) : accounts.length === 0 ? (
          <Text>
            Assign a bank account to this HOA before recording checks.
          </Text>
        ) : (
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Account</Label>
                <FieldPicker
                  label="Account"
                  value={accountId}
                  onChange={setAccountId}
                  placeholder="Select an account"
                  options={accounts.map((a) => ({
                    value: a.id,
                    label: `${purposeLabel(a.purpose)} — ${a.accountNumber}`,
                  }))}
                />
              </Field>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <Field>
                  <Label>Check #</Label>
                  <Input
                    name="check_number"
                    inputMode="numeric"
                    value={checkNumber}
                    onChange={(e) => setCheckNumber(e.target.value)}
                  />
                </Field>
                <Field>
                  <Label>Amount (USD)</Label>
                  <Input
                    name="amount"
                    type="text"
                    inputMode="decimal"
                    placeholder="0.00"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                  />
                </Field>
                <Field>
                  <Label>Issue date</Label>
                  <Input
                    name="issue_date"
                    type="date"
                    value={issueDate}
                    onChange={(e) => setIssueDate(e.target.value)}
                  />
                </Field>
              </div>
              <Field>
                <Label>Payee</Label>
                <FieldPicker
                  label="Payee"
                  value={payeeChoice}
                  onChange={setPayeeChoice}
                  options={[
                    { value: OTHER_PAYEE, label: 'Other (type a name)' },
                    ...vendors.map((v) => ({ value: v.id, label: v.name })),
                  ]}
                />
              </Field>
              {payeeChoice === OTHER_PAYEE && (
                <Field>
                  <Label>Payee name</Label>
                  <Input
                    name="payee_name"
                    maxLength={40}
                    value={payeeName}
                    onChange={(e) => setPayeeName(e.target.value)}
                    placeholder="As written on the check"
                  />
                </Field>
              )}
              <Field>
                <Label>Memo</Label>
                <Input
                  name="memo"
                  value={memo}
                  onChange={(e) => setMemo(e.target.value)}
                  placeholder="Optional — for the books only"
                />
              </Field>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field>
                  <Label>Expense account</Label>
                  <FieldPicker
                    label="Expense account"
                    value={expenseId || NO_EXPENSE}
                    onChange={(v) => setExpenseId(v === NO_EXPENSE ? '' : v)}
                    options={[
                      { value: NO_EXPENSE, label: 'Do not post (register only)' },
                      ...expenseAccounts.map((a) => ({
                        value: a.id,
                        label: `${a.number} — ${a.name}`,
                      })),
                    ]}
                  />
                </Field>
                <Field>
                  <Label>Fund</Label>
                  <FieldPicker
                    label="Fund"
                    value={fundId}
                    onChange={setFundId}
                    disabled={!expenseId}
                    options={funds.map((f) => ({ value: f.id, label: f.name }))}
                  />
                </Field>
              </div>
              <Text className="text-xs">
                The expense is debited and the account’s cash is credited, in
                the fund you choose. Without an expense account the check is
                registered with the bank only.
              </Text>
            </FieldGroup>
          </Fieldset>
        )}
      </DialogBody>
      <DialogActions>
        <Button plain onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          color="sky"
          onClick={handleSubmit}
          disabled={submitting || loading || !!loadError || accounts.length === 0}
        >
          {submitting ? 'Recording…' : 'Record check'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CheckDialog;
